/**
 * Links website visits to HubSpot leads.
 *
 * HubSpot builds a contact's journey (first page seen, page views, last page seen) from
 * the page views its tracking code records under the `hubspotutk` cookie. Two things
 * are needed for that journey to reach the contact:
 *  1. every page view is recorded — Next.js changes pages without a reload, and the
 *     tracking code only records the page it loaded on, so later pages are reported here;
 *  2. the form submission carries the same visitor token (`context.hutk`) — without it
 *     HubSpot creates the contact from the form page alone.
 */

// Declared with `var` so they are typed on `globalThis` (interface Window members are not).
declare global {
    /** HubSpot tracking-code command queue. */
    // eslint-disable-next-line no-var
    var _hsq: unknown[] | undefined;
    /** Set by the HubSpot tracking code once it has started and processed `_hsq`. */
    // eslint-disable-next-line no-var
    var _hstc_ran: boolean | undefined;
}

/** HubSpot visitor tokens are 32 hex characters; anything else is rejected with INVALID_HUTK. */
const HUTK_PATTERN = /^[0-9a-f]{32}$/i;

/** The visitor's HubSpot token, or undefined when the tracking code has not set it (blocked, declined, not loaded yet). */
export const getHubspotUtk = (): string | undefined => {
    if (typeof document === 'undefined') return undefined;
    const value = /(?:^|;\s*)hubspotutk=([^;]*)/.exec(document.cookie)?.[1];
    return value && HUTK_PATTERN.test(value) ? value : undefined;
};

/** `context.hutk` for a form submission — omitted entirely when unknown, since HubSpot rejects an invalid token. */
export const hubspotUtkContext = (): {hutk?: string} => {
    const hutk = getHubspotUtk();
    return hutk ? {hutk} : {};
};

let lastTrackedPath: string | undefined;

/**
 * Records a client-side page change in HubSpot. Call after every route change.
 *
 * Skipped until the tracking code has started: on load it records the current page by
 * itself, and commands queued before then are replayed out of order (every `setPath`
 * first, then every `trackPageView`), which would log the wrong page several times.
 */
export const trackHubspotPageView = (path: string): void => {
    if (globalThis.window === undefined || !globalThis._hstc_ran || !globalThis._hsq) return;
    const cleanPath = path.split('#')[0] || '/';
    if (cleanPath === lastTrackedPath) return;
    lastTrackedPath = cleanPath;
    // One command per push: once started, HubSpot replaces `push` with a handler that
    // reads only its first argument.
    globalThis._hsq.push(['setPath', cleanPath]);
    globalThis._hsq.push(['trackPageView']);
};
